import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as borderouSvc from '../../services/borderou';
import { getPontajDay } from '../../services/pontaj';
import { PONTAJ_ENABLED } from '../../config/features';
import {
  createUser as createUserService,
  deleteManagedUser as deleteManagedUserService,
  listUsers as listUsersService,
  updateManagedUser as updateManagedUserService,
  updateProfile as updateProfileService,
} from '../../services/auth';
import { FilterPill } from '../Shared/FilterPill';
import ImportMarciModal from '../Shared/ImportMarciModal';
import { ModalMultiSelect } from '../Shared/TaskModal';
import './Sidebar.css';

// From the AI-agent rules docs (raport_zilnic doc, section 2) — a user can
// hold several of these at once (e.g. both Avocat/Consilier and Facturare).
const ROLE_OPTIONS = [
  { key: 'coordonator', label: 'Coordonator' },
  { key: 'avocat_coordonator', label: 'Avocatul coordonator' },
  { key: 'avocat_consilier', label: 'Avocat / Consilier' },
  { key: 'facturare', label: 'Facturare' },
  { key: 'utilizator', label: 'Utilizator' },
];

const IconUsers = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const IconGrid = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M9 3v18M3 9h18"/>
  </svg>
);

const IconPerson = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconCard = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="5" width="20" height="14" rx="2"/>
    <path d="M2 10h20"/>
  </svg>
);

const IconSearch = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const IconHome = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const IconCalendar = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <path d="M16 2v4M8 2v4M3 10h18"/>
  </svg>
);

const IconClock = () => (
  <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 7v5l3 3"/>
  </svg>
);

const IconLink = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const Sidebar = ({ mobileOpen = false, onMobileClose = () => {} }) => {
  const { user, logoutUser, updateUser, viewMode, setViewMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [viewDropOpen, setViewDropOpen] = useState(false);
  const viewDropRef = useRef(null);
  useEffect(() => {
    if (!viewDropOpen) return;
    const handle = (e) => { if (viewDropRef.current && !viewDropRef.current.contains(e.target)) setViewDropOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [viewDropOpen]);
  const [reserveSold, setReserveSold] = useState(borderouSvc.getCachedSold());
  useEffect(() => {
    if (borderouSvc.getCachedSold() === null) borderouSvc.getReserve().catch(() => {});
    return borderouSvc.subscribeReserve(setReserveSold);
  }, []);
  const reserveLow = reserveSold !== null && reserveSold <= borderouSvc.RESERVE_LOW_THRESHOLD;

  const isCoordonator = !!user?.roles?.includes('coordonator');
  const [pontajSubmittedToday, setPontajSubmittedToday] = useState(null);
  useEffect(() => {
    if (!PONTAJ_ENABLED || isCoordonator || !user) { setPontajSubmittedToday(null); return; }
    const d = new Date();
    const todayISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    getPontajDay(todayISO).then(res => setPontajSubmittedToday(!!res.submitted_at)).catch(() => {});
  }, [isCoordonator, user]);
  const isAfter4pmWeekday = () => {
    const now = new Date();
    return now.getDay() >= 1 && now.getDay() <= 5 && now.getHours() >= 16;
  };
  const pontajAlert = !isCoordonator && pontajSubmittedToday === false && isAfter4pmWeekday();

  const [oficiiOpen, setOficiiOpen] = useState(() => location.pathname.startsWith('/oficii'));
  const [litigiiOpen, setLitigiiOpen] = useState(() => location.pathname.startsWith('/litigii'));
  const [cercOpen, setCercOpen] = useState(false);
  const [importMarciOpen, setImportMarciOpen] = useState(false);

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', username: '', email: '', password: '', password_confirm: '' });
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersSuccess, setUsersSuccess] = useState('');
  const [isUserSaving, setIsUserSaving] = useState(false);
  const [managedUserForm, setManagedUserForm] = useState({ id: null, username: '', first_name: '', last_name: '', password: '', is_superuser: false, societate: 'alienzaz', roles: [] });

  const handleLogout = async () => {
    try { await logoutUser(); } finally { navigate('/login', { replace: true }); }
  };

  const openProfileModal = () => {
    setProfileForm({ first_name: user?.first_name || '', last_name: user?.last_name || '', username: user?.username || '', email: user?.email || '', password: '', password_confirm: '' });
    setProfileError(''); setProfileSuccess('');
    setIsProfileModalOpen(true);
  };

  const closeProfileModal = () => {
    setIsProfileModalOpen(false);
    setProfileError(''); setProfileSuccess('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError(''); setProfileSuccess('');
    setIsSubmitting(true);
    if (profileForm.password && profileForm.password !== profileForm.password_confirm) {
      setProfileError('Parolele nu se potrivesc');
      setIsSubmitting(false);
      return;
    }
    try {
      const data = await updateProfileService({ user_id: user?.id, first_name: profileForm.first_name, last_name: profileForm.last_name, username: profileForm.username, email: profileForm.email, password: profileForm.password || undefined });
      if (data?.user) updateUser(data.user);
      setProfileSuccess('Profil actualizat cu succes');
      setTimeout(() => closeProfileModal(), 1500);
    } catch (err) {
      setProfileError(err.message || 'Nu am putut actualiza profilul');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetManagedUserForm = () => setManagedUserForm({ id: null, username: '', first_name: '', last_name: '', email: '', password: '', is_superuser: false, societate: 'alienzaz', roles: [] });

  const loadUsers = async () => {
    setUsersLoading(true); setUsersError('');
    try { const data = await listUsersService(user?.id); setUsersList(data?.users || []); }
    catch (err) { setUsersError(err.message || 'Nu am putut incarca utilizatorii'); }
    finally { setUsersLoading(false); }
  };

  const openUsersModal = async () => {
    setUsersError(''); setUsersSuccess(''); resetManagedUserForm();
    setIsUsersModalOpen(true); await loadUsers();
  };

  const closeUsersModal = () => { setIsUsersModalOpen(false); setUsersError(''); setUsersSuccess(''); resetManagedUserForm(); };

  const startEditManagedUser = (entry) => {
    setUsersError(''); setUsersSuccess('');
    setManagedUserForm({ id: entry.id, username: entry.username || '', first_name: entry.first_name || '', last_name: entry.last_name || '', email: entry.email || '', password: '', is_superuser: !!entry.is_superuser, societate: entry.societate || 'alienzaz', roles: entry.roles || [] });
  };

  const handleManagedUserSubmit = async (e) => {
    e.preventDefault();
    setUsersError(''); setUsersSuccess('');
    if (!managedUserForm.username.trim()) { setUsersError('Username este obligatoriu'); return; }
    if (!managedUserForm.id && !managedUserForm.password) { setUsersError('Parola este obligatorie la crearea unui utilizator'); return; }
    setIsUserSaving(true);
    try {
      const payload = { actor_user_id: user?.id, username: managedUserForm.username.trim(), first_name: managedUserForm.first_name, last_name: managedUserForm.last_name, email: managedUserForm.email, password: managedUserForm.password || undefined, is_superuser: managedUserForm.is_superuser, societate: managedUserForm.societate, roles: managedUserForm.roles };
      let response;
      if (managedUserForm.id) { response = await updateManagedUserService(managedUserForm.id, payload); setUsersSuccess('Utilizator actualizat cu succes'); }
      else { response = await createUserService(payload); setUsersSuccess('Utilizator creat cu succes'); }
      if (response?.user?.id === user?.id) updateUser(response.user);
      resetManagedUserForm(); await loadUsers();
    } catch (err) { setUsersError(err.message || 'Operatiunea a esuat'); }
    finally { setIsUserSaving(false); }
  };

  const handleDeleteManagedUser = async (targetUserId) => {
    if (!window.confirm('Sigur vrei sa stergi acest utilizator?')) return;
    setUsersError(''); setUsersSuccess('');
    try { await deleteManagedUserService(targetUserId, user?.id); setUsersSuccess('Utilizator sters'); await loadUsers(); if (managedUserForm.id === targetUserId) resetManagedUserForm(); }
    catch (err) { setUsersError(err.message || 'Nu am putut sterge utilizatorul'); }
  };

  const displayName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username : '';

  const effectiveSocietate = user?.is_superuser ? viewMode : (user?.societate || '');
  const brandName = effectiveSocietate === 'inventa' ? 'Inventa IPMS' : 'Alienzaz IPMS';

  return (
    <>
      {mobileOpen && <div className="sidebar-overlay" onClick={onMobileClose} />}
      <nav className={`sidebar${mobileOpen ? ' sidebar-mobile-open' : ''}`}>
        <div className="brand-logo">
          <div className="brand-dot" />
          <div>
            <div className="brand-name">{brandName}</div>
          </div>
          {mobileOpen && (
            <button className="sidebar-close-btn" type="button" onClick={onMobileClose} aria-label="Inchide meniu">×</button>
          )}
        </div>

        <div className="menu">
          <NavLink to="/" end className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`}>
            <IconHome />
            Acasa
          </NavLink>

          <NavLink to="/clienti" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`}>
            <IconUsers />
            Clienti
          </NavLink>

          <div className={`menu-item${oficiiOpen ? ' open' : ''}`} onClick={() => setOficiiOpen(o => !o)}>
            <IconGrid />
            Oficii
            <span className="menu-arrow">›</span>
          </div>
          {oficiiOpen && (
            <div className="menu-sub visible">
              {[
                { name: 'OSIM',                path: '/oficii/osim' },
                { name: 'EUIPO',              path: '/oficii/euipo' },
                { name: 'WIPO',               path: '/oficii/wipo' },
                { name: 'EPO',                path: '/oficii/epo' },
                { name: 'Benelux',            path: '/oficii/benelux' },
                { name: 'Marci Internationale', path: '/oficii/internationala' },
              ].map(({ name, path }) => (
                <NavLink key={name} to={path} className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>
                  {name}
                </NavLink>
              ))}
            </div>
          )}

          <div className={`menu-item${litigiiOpen ? ' open' : ''}`} onClick={() => setLitigiiOpen(o => !o)}>
            <svg className="mi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3L3 9v12h6v-6h6v6h6V9z"/>
            </svg>
            Litigii
            <span className="menu-arrow">›</span>
          </div>
          {litigiiOpen && (
            <div className="menu-sub visible">
              <NavLink to="/litigii/dosare" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>Dosare</NavLink>
              <NavLink to="/litigii/termene" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>Termene</NavLink>
            </div>
          )}

          <NavLink to="/calendar" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>
            <IconCalendar />
            Calendar
          </NavLink>


          <NavLink to="/mandatari" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>
            <IconPerson />
            Mandatari Externi
          </NavLink>

          <NavLink to="/facturare" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>
            <IconCard />
            Facturare
            {reserveLow && <span className="menu-dot-alert" title="Fonduri rezerva sub 500 RON" />}
          </NavLink>

          <div className={`menu-item${cercOpen ? ' open' : ''}`} onClick={() => setCercOpen(o => !o)}>
            <IconSearch />
            Cercetari Documentare
            <span className="menu-arrow">›</span>
          </div>
          {PONTAJ_ENABLED && (
            <>
            {isCoordonator ? (
              <NavLink to="/pontaj-angajati" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>
                <IconClock />
                Pontaj Angajati
              </NavLink>
            ) : (
              <NavLink to="/pontaj" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>
                <IconClock />
                Pontaj
                {pontajAlert && <span className="menu-dot-alert" title="Nu ai trimis pontajul de azi" />}
              </NavLink>
            )}
            </>
          )}
          {cercOpen && (
            <div className="menu-sub visible">
              <NavLink to="/reinnoiri" className={({ isActive }) => `menu-item${isActive ? ' active' : ''}`} onClick={onMobileClose}>
                Reinnoiri
              </NavLink>
              {['IP Audit', 'Strategie IP', 'Oferte Colaborare'].map(name => (
                <div key={name} className="menu-item">{name}</div>
              ))}
              <div className="menu-item" onClick={() => { setImportMarciOpen(true); onMobileClose(); }}>
                Importa Marci
              </div>
            </div>
          )}

          {importMarciOpen && <ImportMarciModal onClose={() => setImportMarciOpen(false)} />}

          <div className="sidebar-divider" />
          <div className="surf-link-wrap">
            <button className="surf-link" type="button">
              <IconLink />
              Link Surf
            </button>
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user-row">
            <div className="sidebar-user-name">{displayName}</div>
            <div className="sidebar-user-actions">
              {user?.is_superuser && (
                <div className="sb-view-drop" ref={viewDropRef}>
                  <button
                    type="button"
                    className="sb-view-trigger"
                    onClick={() => setViewDropOpen(o => !o)}
                  >
                    <span>{viewMode === 'alienzaz' ? 'Alienzaz / Cabinet' : viewMode === 'inventa' ? 'Inventa' : 'Toate societatile'}</span>
                    <span className={`sb-view-caret${viewDropOpen ? ' open' : ''}`}>▾</span>
                  </button>
                  {viewDropOpen && (
                    <div className="sb-view-options">
                      {[['', 'Toate societatile'], ['alienzaz', 'Alienzaz / Cabinet'], ['inventa', 'Inventa']].map(([val, label]) => (
                        <div
                          key={val}
                          className={`sb-view-option${viewMode === val ? ' active' : ''}`}
                          onMouseDown={() => { setViewMode(val); setViewDropOpen(false); }}
                        >{label}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {user?.is_superuser && (
                <button type="button" className="sb-action-btn" onClick={() => navigate('/rapoarte')}>Rapoarte</button>
              )}
              {user?.is_superuser && (
                <button type="button" className="sb-action-btn" onClick={openUsersModal}>Utilizatori</button>
              )}
              <button type="button" className="sb-action-btn" onClick={openProfileModal}>Profil</button>
              <button type="button" className="sb-action-btn sb-logout-btn" onClick={handleLogout}>Logout</button>
            </div>
          </div>
        </div>
      </nav>

      {isProfileModalOpen && (
        <div className="profile-modal-overlay">
          <div className="profile-modal">
            <div className="profile-modal-header">
              <h2>Editeaza Profil</h2>
              <button type="button" className="profile-modal-close" onClick={closeProfileModal}>×</button>
            </div>
            <div className="profile-modal-body">
              {profileError && <div className="profile-error">{profileError}</div>}
              {profileSuccess && <div className="profile-success">{profileSuccess}</div>}
              <form onSubmit={handleProfileSubmit}>
                <div className="profile-form-field"><label>Prenume</label><input type="text" value={profileForm.first_name} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))} placeholder="Prenume" /></div>
                <div className="profile-form-field"><label>Nume</label><input type="text" value={profileForm.last_name} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))} placeholder="Nume" /></div>
                <div className="profile-form-field"><label>Username</label><input type="text" value={profileForm.username} onChange={e => setProfileForm(p => ({ ...p, username: e.target.value }))} placeholder="Username" /></div>
                <div className="profile-form-field"><label>Email</label><input type="email" value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" /></div>
                <div className="profile-form-field"><label>Parola noua (optional)</label><input type="password" value={profileForm.password} onChange={e => setProfileForm(p => ({ ...p, password: e.target.value }))} placeholder="Lasa gol daca nu schimbi" /></div>
                <div className="profile-form-field"><label>Confirma parola</label><input type="password" value={profileForm.password_confirm} onChange={e => setProfileForm(p => ({ ...p, password_confirm: e.target.value }))} placeholder="Confirma parola" /></div>
                <div className="profile-form-actions">
                  <button type="submit" className="profile-btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Se actualizeaza...' : 'Salveaza'}</button>
                  <button type="button" className="profile-btn-secondary" onClick={closeProfileModal} disabled={isSubmitting}>Anuleaza</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {isUsersModalOpen && (
        <div className="profile-modal-overlay">
          <div className="profile-modal users-modal">
            <div className="profile-modal-header">
              <h2>Gestionare utilizatori</h2>
              <button type="button" className="profile-modal-close" onClick={closeUsersModal}>×</button>
            </div>
            <div className="profile-modal-body">
              {usersError && <div className="profile-error">{usersError}</div>}
              {usersSuccess && <div className="profile-success">{usersSuccess}</div>}
              <form onSubmit={handleManagedUserSubmit} className="users-form">
                <div className="profile-form-field"><label>Username</label><input type="text" value={managedUserForm.username} onChange={e => setManagedUserForm(p => ({ ...p, username: e.target.value }))} placeholder="Username" /></div>
                <div className="profile-form-field"><label>Prenume</label><input type="text" value={managedUserForm.first_name} onChange={e => setManagedUserForm(p => ({ ...p, first_name: e.target.value }))} placeholder="Prenume" /></div>
                <div className="profile-form-field"><label>Nume</label><input type="text" value={managedUserForm.last_name} onChange={e => setManagedUserForm(p => ({ ...p, last_name: e.target.value }))} placeholder="Nume" /></div>
                <div className="profile-form-field"><label>Email</label><input type="email" value={managedUserForm.email} onChange={e => setManagedUserForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" /></div>
                <div className="profile-form-field"><label>{managedUserForm.id ? 'Parola noua (optional)' : 'Parola'}</label><input type="password" value={managedUserForm.password} onChange={e => setManagedUserForm(p => ({ ...p, password: e.target.value }))} placeholder={managedUserForm.id ? 'Lasa gol pentru a pastra' : 'Parola'} /></div>
                <div className="profile-form-field">
                  <label>Societate</label>
                  <FilterPill value={managedUserForm.societate} onChange={e => setManagedUserForm(p => ({ ...p, societate: e.target.value }))}>
                    <option value="">— Societate —</option>
                    <option value="alienzaz">Alienzaz/Cabinet</option>
                    <option value="inventa">Inventa</option>
                  </FilterPill>
                </div>
                <div className="profile-form-field"><label><input type="checkbox" checked={!!managedUserForm.is_superuser} onChange={e => setManagedUserForm(p => ({ ...p, is_superuser: e.target.checked }))} />{' '}Este admin?</label></div>
                <div className="profile-form-field">
                  <label>Roluri</label>
                  <ModalMultiSelect
                    label="Selecteaza roluri"
                    options={ROLE_OPTIONS}
                    value={managedUserForm.roles}
                    onChange={roles => setManagedUserForm(p => ({ ...p, roles }))}
                    keyField="key"
                    labelField="label"
                  />
                </div>
                <div className="profile-form-actions">
                  <button type="submit" className="profile-btn-primary" disabled={isUserSaving}>{isUserSaving ? 'Se salveaza...' : managedUserForm.id ? 'Actualizeaza' : 'Adauga'}</button>
                  {managedUserForm.id && <button type="button" className="profile-btn-secondary" onClick={resetManagedUserForm} disabled={isUserSaving}>Renunta editare</button>}
                </div>
              </form>
              <div className="users-list-wrap">
                <h3>Utilizatori existenti</h3>
                {usersLoading ? <p>Se incarca...</p> : (
                  <div className="users-list">
                    {usersList.map(entry => (
                      <div className="users-list-item" key={entry.id}>
                        <div>
                          <strong>{entry.username}</strong>
                          <p>{`${entry.first_name || ''} ${entry.last_name || ''}`.trim() || '—'}</p>
                          {entry.email && <p style={{fontSize:'11px',color:'#6b7280',marginTop:'2px'}}>{entry.email}</p>}
                          {entry.societate && <p style={{fontSize:'11px',color:'#6366f1',marginTop:'2px'}}>{entry.societate === 'alienzaz' ? 'Alienzaz/Cabinet' : 'Inventa'}</p>}
                          {entry.roles?.length > 0 && (
                            <p style={{fontSize:'11px',color:'#059669',marginTop:'2px'}}>
                              {entry.roles.map(r => ROLE_OPTIONS.find(o => o.key === r)?.label || r).join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="users-item-actions">
                          <button type="button" className="profile-btn-secondary users-small-btn" onClick={() => startEditManagedUser(entry)}>Edit</button>
                          <button type="button" className="profile-btn-secondary users-small-btn users-delete-btn" onClick={() => handleDeleteManagedUser(entry.id)}>Sterge</button>
                        </div>
                      </div>
                    ))}
                    {!usersList.length && <p>Nu exista utilizatori.</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
